import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { exportScoutingMarketComparePdf } from "@/lib/export";
import RffmSeasonsCompare from "@/components/RffmSeasonsCompare";
import { PRIORITY_BADGE, STATUS_BADGE } from "@/components/ScoutingPlayersTable";

const GOLD = "#D9B300";

const normSeason = (s) => ({
  season: s.season, team: s.team || "", category: s.category || "",
  callups: s.callups ?? null, matches: s.matches_played ?? null, starts: s.starts ?? null,
  goals: s.goals ?? null, minutes: s.participation?.minutes_est ?? null,
  yellow: s.yellow_cards ?? null, red: s.red_cards ?? null,
});

const METRICS = [
  ["Año", "birth_year", false],
  ["Categoría 26/27", "projected_category_computed", false],
  ["Posición", "position", false],
  ["Rol", "role", false],
  ["Club", "current_team", false],
  ["CONV 3T", "callups_3_seasons", true],
  ["PJ 3T", "matches_3_seasons", true],
  ["TIT 3T", "starts_3_seasons", true],
  ["Goles 3T", "goals_3_seasons", true],
  ["Índice", "scouting_score", true],
  ["Media interna", "internal_rating", true],
];

export const ScoutingCompareDialog = ({ open, onOpenChange, players }) => {
  const [seasonsById, setSeasonsById] = useState({});
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  useEffect(() => {
    if (!open || !players?.length) return undefined;
    let alive = true;
    setLoadingSeasons(true);
    Promise.all(players.map((p) =>
      api.get(`/scouting-market/players/${p.id}`)
        .then((r) => [p.id, (r.data.seasons || []).map(normSeason)])
        .catch(() => [p.id, []]),
    ))
      .then((entries) => { if (alive) setSeasonsById(Object.fromEntries(entries)); })
      .finally(() => { if (alive) setLoadingSeasons(false); });
    return () => { alive = false; };
  }, [open, players]);

  if (!players?.length) return null;
  const best = {};
  METRICS.forEach(([, key, numeric]) => {
    if (!numeric) return;
    const vals = players.map((p) => (typeof p[key] === "number" ? p[key] : null)).filter((v) => v !== null);
    if (vals.length > 1) best[key] = Math.max(...vals);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="scouting-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
            Comparador de jugadores
            <Button
              size="sm"
              className="ml-auto mr-6 bg-purple-600 hover:bg-purple-700 text-white font-bold"
              onClick={() => exportScoutingMarketComparePdf(players.map((p) => p.id))}
              data-testid="scouting-compare-pdf"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="px-2 py-2"></th>
                {players.map((p) => (
                  <th key={p.id} className="px-2 py-2 text-center">
                    <div className="font-black text-purple-900" style={{ fontFamily: "Nunito" }}>{p.player_name}</div>
                    <div className="text-[10px] text-slate-400 font-normal truncate max-w-[160px] mx-auto">
                      {p.current_team || p.reported_team || ""}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {METRICS.map(([label, key, numeric]) => (
                <tr key={key}>
                  <td className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">{label}</td>
                  {players.map((p) => {
                    const v = p[key];
                    const isBest = numeric && best[key] !== undefined && v === best[key];
                    return (
                      <td key={p.id} className="px-2 py-2 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded font-bold ${isBest ? "text-slate-900" : "text-slate-700"}`}
                          style={isBest ? { backgroundColor: `${GOLD}33`, border: `1px solid ${GOLD}` } : {}}
                          data-testid={isBest ? `compare-best-${key}` : undefined}
                        >
                          {v ?? "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500">Prioridad</td>
                {players.map((p) => (
                  <td key={p.id} className="px-2 py-2 text-center">
                    {p.priority ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_BADGE[p.priority] || "bg-slate-100 text-slate-600"}`}>{p.priority}</span>
                    ) : "—"}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500">Estado deportivo</td>
                {players.map((p) => (
                  <td key={p.id} className="px-2 py-2 text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[p.sporting_status] || "bg-slate-100 text-slate-600"}`}>
                      {p.sporting_status || "Pendiente"}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <RffmSeasonsCompare
          loading={loadingSeasons}
          cols={players.map((p) => ({ id: p.id, name: p.player_name, seasons: seasonsById[p.id] || [] }))}
        />
        <p className="text-[11px] text-slate-400 pt-1">
          El índice es una ayuda documental y no sustituye la observación presencial.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ScoutingCompareDialog;
